export interface StableEnterOptions {
  delay: number // 进入阈值，如 200(ms)
}

export interface StableEnter {
  reTarget: (key: string | number, el: HTMLElement | null, run: () => void) => void
  cancel: (key: string | number) => void
  dispose: () => void
}
/**
 * 交互优化: 快速掠过不算 hover ~
 * 适合触发式 hover（如菜单项/表格行/卡片）的稳定进入判定
 * @param {number} opts.delay 进入阈值（毫秒）。仅当在目标元素上持续停留 ≥ delay 才执行回调。
 * @returns {StableEnter}
 * @returns {StableEnter['reTarget']} return.reTarget 切换/安排新的进入判定：`reTarget(key, el, run)`。
 * @returns {StableEnter['cancel']} return.cancel 取消当前 `key` 对应的进入判定。
 * @returns {StableEnter['dispose']} return.dispose 释放内部资源（清理任何未触发的计时）。
 *
 * @example
 * PanelMenu 场景：折叠态 + 有子项时，停留 ≥200ms 才上抛 collapsed-item-hover
 * import { useStableEnter } from '~/composables/useStableEnter'
 *
 * const stableEnter = useStableEnter({ delay: 200 })
 *
 * function onItemEnter(item, evt) {
 *   const el = evt.currentTarget as HTMLElement | null
 *   stableEnter.reTarget(item.key, el, () => emit('collapsed-item-hover', item, evt))
 * }
 *
 * function onItemLeave(item) {
 *   stableEnter.cancel(item.key) // 掠过或切项：取消 pending
 * }
 *
 * onUnmounted(() => {
 *   stableEnter.dispose()
 * })
 */
export const useStableEnter = (opts: StableEnterOptions): StableEnter => {
  const { delay } = opts

  let timer: number | null = null
  let pendingKey: string | number | null = null
  let pendingEl: HTMLElement | null = null
  let pendingRun: (() => void) | null = null

  const clear = () => {
    if (timer != null) {
      clearTimeout(timer)
      timer = null
    }
    pendingKey = null
    pendingEl = null
    pendingRun = null
  }

  const reTarget: StableEnter['reTarget'] = (key, el, run) => {
    // 切到新目标：先清旧的，再安排新的进入阈值计时
    if (timer != null) {
      clearTimeout(timer)
      timer = null
    }
    pendingKey = key
    pendingEl = el
    pendingRun = run

    timer = window.setTimeout(() => {
      // 到点二次校验：仍是同一 key 且元素仍处于 :hover
      const stillThis = pendingKey === key
      const stillHover = !!pendingEl && pendingEl.matches(':hover')
      if (stillThis && stillHover && pendingRun) pendingRun()
      clear()
    }, delay)
  }

  const cancel: StableEnter['cancel'] = (key) => {
    if (pendingKey === key) clear()
  }

  const dispose = () => clear()

  return { reTarget, cancel, dispose }
}
